import { FC, useState, useEffect, useMemo } from 'react'
import { Breadcrumbs, Input } from '@mondra/ui-components'
import { GET_ALL_RESOURCES } from '../../../query/resource'
import { useQuery } from '@apollo/client'
import { IResource } from './types'
import { getResourcesVars } from './helpers'
import { filterEnums, getTableData } from '../../../shared/gridFunctions'
import Table from '../../../components/Table'

import './index.scss'
import { EXPORT_RESOURCES_QUERY } from '../../../query/export-resource'
import { EXPORT_MAX_ROW_COUNT } from '../../../const'
import { ExportGraphqlButton } from '../../../components/ExportGraphqlButton'

const Resources: FC = () => {
  const [tableData, setTableData] = useState<IResource[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [resourcesVars, setResourcesVars] = useState<any>(getResourcesVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, error, refetch } = useQuery(GET_ALL_RESOURCES, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getResourcesVars,
    },
  })

  const anyRefetch = (variables?: any): Promise<any> => {
    return refetch(variables)
  }

  const setCursors = () => {
    setEndCursor(data.resources.pageInfo.endCursor)
    setStartCursor(data.resources.pageInfo.startCursor)
  }

  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '',
      'resources',
      'resource'
    )
  }, [data])

  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setResourcesVars({ ...filterEnums(tableSearch, getResourcesVars, data) })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getResourcesVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading])

  const exportPayload = useMemo(
    () => ({
      operationName: 'Resources',
      query: EXPORT_RESOURCES_QUERY.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: null,
        order,
      },
    }),
    [order]
  )

  return (
    <div className={'packaging'}>
      <div style={{ width: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs routes={[{ path: '/resources', title: 'Resources Table' }]} />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Resources</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={resourcesVars || getResourcesVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default Resources
